import { z } from 'zod';
import type { DocLang, MarginSource, RateUnit } from './enums';

/**
 * Rates: one variable, margin_pct (spec §5).
 *
 * All rates are EUR per hour. A day rate is converted with hourly = daily / hours_per_day before any
 * calculation. Our margin is a share of the client rate.
 */

export const DEFAULT_MARGIN_PCT = 20;
export const DEFAULT_HOURS_PER_DAY = 8;

/** 0 <= margin_pct < 100, at most 2 decimals. */
export const marginPctSchema = z
  .number()
  .min(0, 'margin_pct must be >= 0')
  .lt(100, 'margin_pct must be < 100')
  .refine((v) => Number.isFinite(v) && Math.abs(v * 100 - Math.round(v * 100)) < 1e-9, {
    message: 'margin_pct may have at most 2 decimals',
  });

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function round1(n: number): number {
  return Math.round((n + Number.EPSILON) * 10) / 10;
}

/** Convert a rate to EUR/h (2 decimals). */
export function toHourly(rate: number, unit: RateUnit, hoursPerDay: number = DEFAULT_HOURS_PER_DAY): number {
  if (!Number.isFinite(rate)) throw new Error('rate must be a finite number');
  if (unit === 'DAY') {
    if (!hoursPerDay || hoursPerDay <= 0) throw new Error('hours_per_day must be > 0');
    return round2(rate / hoursPerDay);
  }
  return round2(rate);
}

/** Supplier ceiling (max hourly rate we can accept from a supplier), 2 decimals: client × (1 − m). */
export function supplierCeiling(clientRateH: number, marginPct: number): number {
  assertMargin(marginPct);
  return round2(clientRateH * (1 - marginPct / 100));
}

/** Supplier ceiling as displayed: rounded down to a whole euro. */
export function supplierCeilingDisplay(clientRateH: number, marginPct: number): number {
  return Math.floor(supplierCeiling(clientRateH, marginPct) + 1e-9);
}

/** Client offer for a candidate: candidate_h / (1 − m), rounded UP to the next whole euro. */
export function clientOffer(candidateRateH: number, marginPct: number): number {
  assertMargin(marginPct);
  const raw = candidateRateH / (1 - marginPct / 100);
  return Math.ceil(round2(raw) - 1e-9);
}

/** Raw client offer before rounding (2 decimals), useful to explain the calculation. */
export function clientOfferRaw(candidateRateH: number, marginPct: number): number {
  assertMargin(marginPct);
  return round2(candidateRateH / (1 - marginPct / 100));
}

/** Rate text shown on a JD: "up to € X / h (all-in)" or "on request". German: "bis zu X € / h (all-in)" / "auf Anfrage". */
export function jdRateText(
  clientRateH: number | null | undefined,
  marginPct: number,
  lang: DocLang | 'en' | 'de' = 'EN',
): string {
  const de = String(lang).toUpperCase() === 'DE';
  if (
    clientRateH === null ||
    clientRateH === undefined ||
    !Number.isFinite(clientRateH) ||
    clientRateH <= 0
  ) {
    return de ? 'auf Anfrage' : 'on request';
  }
  const x = supplierCeilingDisplay(clientRateH, marginPct);
  return de ? `bis zu ${x} € / h (all-in)` : `up to € ${x} / h (all-in)`;
}

/** Actual margin of a submission: (client_offer − candidate_h) / client_offer × 100, 1 decimal. */
export function actualMarginPct(clientOfferH: number, candidateRateH: number): number | null {
  if (!clientOfferH || clientOfferH <= 0) return null;
  return round1(((clientOfferH - candidateRateH) / clientOfferH) * 100);
}

export interface MarginLevels {
  /** submission.margin_pct */
  submission?: number | null;
  /** position.margin_pct */
  position?: number | null;
  /** settings.margin_pct (never empty) */
  global: number;
}

export interface EffectiveMargin {
  marginPct: number;
  source: MarginSource;
}

/**
 * Effective margin = submission.margin_pct ?? position.margin_pct ?? settings.margin_pct.
 * For a position pass only { position, global }.
 */
export function resolveEffectiveMargin(levels: MarginLevels): EffectiveMargin {
  if (levels.submission !== null && levels.submission !== undefined) {
    return { marginPct: levels.submission, source: 'SUBMISSION' };
  }
  if (levels.position !== null && levels.position !== undefined) {
    return { marginPct: levels.position, source: 'POSITION' };
  }
  return { marginPct: levels.global, source: 'GLOBAL' };
}

export function formatMarginWithSource(m: EffectiveMargin): string {
  const src = m.source === 'GLOBAL' ? 'global' : m.source === 'POSITION' ? 'position' : 'this submission';
  return `${formatPct(m.marginPct)} % (${src})`;
}

export function formatPct(pct: number): string {
  return Number.isInteger(pct) ? String(pct) : String(round2(pct));
}

export function formatEurPerHour(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '–';
  return `${formatNumber(rate)} EUR/h`;
}

export function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : String(round2(n));
}

export interface RateWarning {
  code: 'CANDIDATE_ABOVE_CEILING' | 'ACTUAL_MARGIN_BELOW_TARGET' | 'MARGIN_CHANGED_SINCE_OFFER';
  message: string;
}

export interface RateCheckInput {
  candidateRateH?: number | null;
  clientRateH?: number | null; // position client rate (hourly)
  clientOfferH?: number | null; // submission offer (hourly)
  effectiveMarginPct: number;
  marginPctApplied?: number | null;
}

/** Warnings shown in the UI and in MCP responses (spec §5.2). */
export function rateWarnings(input: RateCheckInput): RateWarning[] {
  const warnings: RateWarning[] = [];
  const { candidateRateH, clientRateH, clientOfferH, effectiveMarginPct, marginPctApplied } = input;
  if (candidateRateH != null && clientRateH != null && clientRateH > 0) {
    const ceiling = supplierCeiling(clientRateH, effectiveMarginPct);
    if (candidateRateH > ceiling + 1e-9) {
      warnings.push({
        code: 'CANDIDATE_ABOVE_CEILING',
        message: `Candidate rate ${formatNumber(candidateRateH)} EUR/h is above the supplier ceiling of ${formatNumber(
          ceiling,
        )} EUR/h (client ${formatNumber(clientRateH)} EUR/h at ${formatPct(effectiveMarginPct)} % margin).`,
      });
    }
  }
  if (candidateRateH != null && clientOfferH != null && clientOfferH > 0) {
    const actual = actualMarginPct(clientOfferH, candidateRateH);
    if (actual !== null && actual < effectiveMarginPct - 1e-9) {
      warnings.push({
        code: 'ACTUAL_MARGIN_BELOW_TARGET',
        message: `Actual margin ${actual} % is below the effective margin of ${formatPct(effectiveMarginPct)} %.`,
      });
    }
  }
  if (marginPctApplied != null && Math.abs(marginPctApplied - effectiveMarginPct) > 1e-9) {
    warnings.push({
      code: 'MARGIN_CHANGED_SINCE_OFFER',
      message: `Margin changed since this offer was calculated (applied ${formatPct(
        marginPctApplied,
      )} %, now ${formatPct(effectiveMarginPct)} %).`,
    });
  }
  return warnings;
}

export interface RateCalculation {
  effectiveMargin: EffectiveMargin;
  hoursPerDay: number;
  clientRateH: number | null;
  supplierCeiling: number | null;
  supplierCeilingDisplay: number | null;
  jdRateText: string;
  candidateRateH: number | null;
  clientOffer: number | null;
  clientOfferRaw: number | null;
  actualMarginPct: number | null;
  warnings: RateWarning[];
}

export interface RateCalculationInput {
  clientRate?: number | null;
  clientRateUnit?: RateUnit | null;
  candidateRate?: number | null;
  candidateRateUnit?: RateUnit | null;
  margin: MarginLevels;
  hoursPerDay?: number;
  lang?: DocLang;
}

/** Everything the UI and `calculate_rates` need, from raw inputs. Pure, nothing is saved. */
export function calculateRates(input: RateCalculationInput): RateCalculation {
  const hoursPerDay = input.hoursPerDay ?? DEFAULT_HOURS_PER_DAY;
  const effectiveMargin = resolveEffectiveMargin(input.margin);
  const m = effectiveMargin.marginPct;
  const clientRateH =
    input.clientRate != null ? toHourly(input.clientRate, input.clientRateUnit ?? 'HOUR', hoursPerDay) : null;
  const candidateRateH =
    input.candidateRate != null
      ? toHourly(input.candidateRate, input.candidateRateUnit ?? 'HOUR', hoursPerDay)
      : null;
  const offer = candidateRateH != null ? clientOffer(candidateRateH, m) : null;
  return {
    effectiveMargin,
    hoursPerDay,
    clientRateH,
    supplierCeiling: clientRateH != null ? supplierCeiling(clientRateH, m) : null,
    supplierCeilingDisplay: clientRateH != null ? supplierCeilingDisplay(clientRateH, m) : null,
    jdRateText: jdRateText(clientRateH, m, input.lang ?? 'EN'),
    candidateRateH,
    clientOffer: offer,
    clientOfferRaw: candidateRateH != null ? clientOfferRaw(candidateRateH, m) : null,
    actualMarginPct: offer != null && candidateRateH != null ? actualMarginPct(offer, candidateRateH) : null,
    warnings: rateWarnings({
      candidateRateH,
      clientRateH,
      clientOfferH: offer,
      effectiveMarginPct: m,
    }),
  };
}

function assertMargin(marginPct: number): void {
  if (!Number.isFinite(marginPct) || marginPct < 0 || marginPct >= 100) {
    throw new Error(`margin_pct must be within [0, 100): got ${marginPct}`);
  }
}
